import {
  IArrayValidationDescription,
  IObjectValidationDescription,
  ISchema,
  IValidationDescription,
  describeSchema,
} from '@framework/validation';
import { fromPairs } from 'lodash';
import { multiSelectOptionsObjectName } from '../../schema/widgets/repository/inputs/multi-select/getMultiSelectOptionsObjectName';
import { FormBuilderMetaFlags } from '../form-builder/flags/FormBuilderMetaFlags';
import { deserializeFormSectionStatus } from '../status/serializeFormSectionsStatus';
import {
  ISerializedFormValue,
  ISerializedRootFormValue,
} from './serializeFormValues';

export function deserializeFormValues(
  schema: ISchema<any>,
  serializedFormValues: ISerializedRootFormValue,
) {
  const description = describeSchema(schema, {});
  return deserializeFormRoot(description, serializedFormValues);
}

function deserializeFormRoot(
  schema: IValidationDescription,
  serialized: ISerializedRootFormValue,
): Record<string, any> | null {
  // if (isRoot && !('values' in serialized)) {
  //   throw new Error('Modulo serializzato non valido, manca il campo "values" nella root'); // prettier-ignore
  // }

  if (!serialized) return null;

  const deserialized = deserializeObject(
    schema as IObjectValidationDescription,
    serialized.values,
  );
  return {
    ...deserialized,
    ...deserializeFormSectionStatus(serialized.sections),
  };
}

function deserializeObject(
  schema: IValidationDescription | null,
  values: ISerializedFormValue[] | undefined,
) {
  if (!values) return {};
  return Object.fromEntries(
    values.flatMap(serialized =>
      deserializeFormSchema(
        serialized?.name && schema?.type === 'object'
          ? schema?.schemas[serialized.name]
          : null,
        serialized,
      ),
    ),
  );
}

function deserializeArray(
  schema: IArrayValidationDescription | null,
  items: ISerializedFormValue[] | undefined,
): any[] {
  if (!items) return [];
  return items.map((item, i) => {
    if (!item || !('type' in item)) return item;
    // Se non c'è uno schema (es perché è un array vuoto), usiamo il primo
    // schema disponibile. Ne generiamo sempre _almeno uno_ con `value=undefined`
    // in `describeSchema`.
    const itemSchema = schema?.schemas[i] ?? schema?.schemas[0] ?? null;
    switch (item.type) {
      case 'object':
        return deserializeObject(itemSchema, item.values);
      case 'array':
        return deserializeArray(
          itemSchema as IArrayValidationDescription,
          item.items,
        );
      default:
        return item.value;
    }
  });
}

type IDeserializationTuple = [name: string, value: any];

function deserializeFormSchema(
  schema: IValidationDescription | null | undefined,
  serialized: ISerializedFormValue,
): IDeserializationTuple[] {
  if (!serialized || !('type' in serialized) || !serialized.name) {
    return [];
  }

  switch (serialized.type) {
    case 'object': {
      return [
        [
          serialized.name,
          deserializeObject(
            schema?.type === 'object' ? schema : null,
            serialized.values,
          ),
        ],
      ];
    }
    case 'array': {
      return [
        [
          serialized.name,
          deserializeArray(
            schema?.type === 'array' ? schema : null,
            serialized.items,
          ),
        ],
      ];
    }
    default: {
      const tuples: IDeserializationTuple[] = [];
      tuples.push([serialized.name, serialized.value]);

      // Meccanismo per serializzare le opzioni di un widget array;
      // dato che sono registrate in un campo fratello, dobbiamo
      // gestirlo manualmente.
      const arraySchema = schema?.type === 'array' ? schema : null;
      const serializeOptions = arraySchema?.desc.spec.meta?.[
        FormBuilderMetaFlags.SerializeSiblingOptions
      ] as string[] | undefined;

      if (serializeOptions) {
        const optionsObject = fromPairs(
          serializeOptions.map(option => [option, false]),
        );
        for (const selected of serialized.value ?? []) {
          optionsObject[selected] = true;
        }

        tuples.push([
          multiSelectOptionsObjectName(serialized.name),
          optionsObject,
        ]);
      }

      return tuples;
    }
  }
}
